import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, switchMap, filter, take, finalize } from 'rxjs/operators';
import { ProgressService } from 'src/app/shared/progress';
import { AuthenticationService, RefreshTokenPersistenceService, UserSessionPersistenceService } from '../services';
import { endpoints } from '@environments/endpoints';
import { UserSession } from '@core/models';
import { Router } from '@angular/router';

const isRefreshing = new BehaviorSubject<boolean>(false);
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

export function authInterceptor(
    request: HttpRequest<any>,
    next: HttpHandlerFn
): Observable<HttpEvent<any>> {
    const progressService = inject(ProgressService);
    const userSessionPersistenceService = inject(UserSessionPersistenceService);
    const refreshTokenPersistenceService = inject(RefreshTokenPersistenceService);
    const authenticationService = inject(AuthenticationService);
    const router = inject(Router);

    progressService.start();

    var initialUrl = request.url;

    let currentUserSession = userSessionPersistenceService.getUserSession();
    if (currentUserSession) {
        currentUserSession.refreshToken = refreshTokenPersistenceService.get();
        request = addHeaders(request, currentUserSession);
    }


    return next(request).pipe(
        catchError((err: HttpErrorResponse) => {
            if (err instanceof HttpErrorResponse && err.status === 401) {
                if (request.url.indexOf(endpoints.v1.authRefreshToken) >= 0) {
                    userSessionPersistenceService.removeUserSession();
                    refreshTokenPersistenceService.remove();

                    router.navigate(['/auth/login'], {
                        queryParams: { returnUrl: initialUrl },
                    });

                    return throwError(() => err);
                }

                return handle401Error(
                    request,
                    next,
                    currentUserSession,
                    isRefreshing,
                    refreshTokenSubject,
                    userSessionPersistenceService,
                    refreshTokenPersistenceService,
                    authenticationService
                );
            } else {
                return throwError(() => err);
            }
        }),
        finalize(() => progressService.complete())
    );
}

function handle401Error(
    request: HttpRequest<any>,
    next: HttpHandlerFn,
    currentUserSession: UserSession | undefined,
    isRefreshing: BehaviorSubject<boolean>,
    refreshTokenSubject: BehaviorSubject<string | null>,
    userSessionPersistenceService: UserSessionPersistenceService,
    refreshTokenPersistenceService: RefreshTokenPersistenceService,
    authenticationService: AuthenticationService
): Observable<HttpEvent<any>> {
    currentUserSession = currentUserSession ?? new UserSession();
    currentUserSession.refreshToken = refreshTokenPersistenceService.get();

    if (!isRefreshing.value) {
        isRefreshing.next(true);
        refreshTokenSubject.next(null);

        return authenticationService.refreshToken(currentUserSession).pipe(
            switchMap((userSession: UserSession) => {
                isRefreshing.next(false);
                currentUserSession = { ...currentUserSession, ...userSession };

                userSessionPersistenceService.setUserSession(currentUserSession);
                refreshTokenPersistenceService.set(userSession.refreshToken!, currentUserSession.expirationDate);
                refreshTokenSubject.next(userSession.token ?? null);

                if (request.url.indexOf(endpoints.v1.authLogout) >= 0 && request.body instanceof UserSession) {
                    return next(addHeaders(handleLogout(request, userSession.refreshToken!), currentUserSession));
                }

                return next(addHeaders(request, currentUserSession));
            })
        );
    } else {
        return refreshTokenSubject.pipe(
            filter((token) => token != null),
            take(1),
            switchMap((jwt) => {
                currentUserSession!.token = jwt!;
                return next(addHeaders(request, currentUserSession!));
            })
        );
    }
}

function handleLogout(request: HttpRequest<any>, refreshToken: string) {
    var logoutUserSession = request.body as UserSession;
    logoutUserSession.refreshToken = refreshToken;
    var newRequest = request.clone({
        body: logoutUserSession
    });
    return newRequest;
}

function addHeaders(request: HttpRequest<any>, userSession: UserSession): HttpRequest<any> {
    if (request.url.indexOf(endpoints.v1.authRefreshToken) >= 0) {
        return request.clone();
    }
    return request.clone({
        setHeaders: {
            Authorization: `Bearer ${userSession.token}`,
        },
    });
}
